import React, { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import ProtectedRoute from "../Components/Common/ProtectedRoute";
import "../pages/Wiki/wiki.css";

// Wrapper component to add Suspense to lazy loaded components
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={null}>
    <Component />
  </Suspense>
);

// Dashboard
const ExecutiveDashboard = lazy(() => import("../pages/Dashboards/ExecutiveDashboard"));

// Projects
const Projects = lazy(() => import("../pages/Projects"));
const ProjectOverview = lazy(() => import("../pages/Projects/ProjectOverview"));
const ProjectManagerOverview = lazy(() => import("../pages/Projects/ProjectManagerOverview"));
const CreateProject = lazy(() => import("../pages/Projects/CreateProject"));
const CreateConfirmedProject = lazy(() => import("../pages/Projects/CreateConfirmedProject"));
const CreateAdaptation = lazy(() => import("../pages/Projects/CreateAdaptation"));
const ProjectTypeSelection = lazy(() => import("../pages/Projects/ProjectTypeSelection"));
const EditProject = lazy(() => import("../pages/Projects/EditProject"));
const ProjectCommentsTest = lazy(() => import("../pages/Projects/ProjectCommentsTest"));
const PitchPlease = lazy(() => import("../pages/Projects/PitchPlease"));
const ProjectGraveyard = lazy(() => import("../pages/Projects/ProjectGraveyard"));

// Calculations
const KalkulationList = lazy(() => import("../pages/Calculations/KalkulationList"));
const KalkulationDetail = lazy(() => import("../pages/Calculations/KalkulationDetail"));
const CalculationTemplates = lazy(() => import("../pages/Calculations/CalculationTemplates"));
const CalculationTemplateEditor = lazy(() => import("../pages/Calculations/CalculationTemplateEditor"));
const CalculationCommentsOverview = lazy(() => import("../pages/Calculations/CalculationCommentsOverview"));
const ProjectHoursOverview = lazy(() => import("../pages/Calculations/ProjectHoursOverview"));
const EmployeeDetail = lazy(() => import("../pages/Calculations/ProjectHoursOverview/EmployeeDetail"));

// Invoices
const InvoiceListPage = lazy(() => import("../pages/Invoices/InvoiceListPage"));
const InvoiceDetails = lazy(() => import("../pages/Invoices/InvoiceDetails"));
const InvoiceNew = lazy(() => import("../pages/Invoices/InvoiceNew"));

// Incoming Invoices
const IncomingInvoicesList = lazy(() => import("../pages/IncomingInvoices/IncomingInvoicesList"));
const MyApprovals = lazy(() => import("../pages/MyApprovals"));

// Document Capture
const DocumentCapture = lazy(() => import("../pages/DocumentCapture"));
const DocumentReview = lazy(() => import("../pages/DocumentReview"));
const MyDocuments = lazy(() => import("../pages/MyDocuments"));

// Emails
const EmailInbox = lazy(() => import("../pages/Emails/EmailInbox"));

// Receipts
const Receipts = lazy(() => import("../pages/Receipts"));

// Cash Book
const CashBook = lazy(() => import("../pages/CashBook"));

// Vendors
const Vendors = lazy(() => import("../pages/Vendors"));

// Expense Reports
const ExpenseReports = lazy(() => import("../pages/ExpenseReports"));
const ExpenseReportDetail = lazy(() => import("../pages/ExpenseReports/ExpenseReportDetail"));

// Subscriptions
const Subscriptions = lazy(() => import("../pages/Subscriptions"));
const SubscriptionsOverview = lazy(() => import("../pages/Subscriptions/SubscriptionsOverview"));

// Bank Statements
const BankStatements = lazy(() => import("../pages/Finanzen/BankStatements"));

// Monthly Report
const MonthlyReport = lazy(() => import("../pages/Finanzen/MonthlyReport"));

// Reports
const Reports = lazy(() => import("../pages/Reports"));
const ArtlistReports = lazy(() => import("../pages/Reports/ArtlistReports"));
const PortfolioReports = lazy(() => import("../pages/Reports/PortfolioReports"));

// Credit Cards
const CreditCards = lazy(() => import("../pages/CreditCards"));
const CreditCardStatements = lazy(() => import("../pages/CreditCards/CreditCardStatements"));
const CreditCardTransactions = lazy(() => import("../pages/CreditCards/CreditCardTransactions"));

// Turnovers
const TurnoverListPage = lazy(() => import("../pages/Turnovers/TurnoverListPage"));

// Payment Reminders
const OverdueInvoices = lazy(() => import("../pages/PaymentReminders/OverdueInvoices"));
const PaymentRemindersManagement = lazy(() => import("../pages/PaymentReminders/PaymentRemindersManagement"));
const PaymentReminderPDF = lazy(() => import("../pages/PaymentReminders/PaymentReminderPDF"));

// Estimates
const EstimateList = lazy(() => import("../pages/Estimates/EstimateList"));
const EstimateDetail = lazy(() => import("../pages/Estimates/EstimateDetail"));
const EstimateSettings = lazy(() => import("../pages/Estimates/EstimateSettings"));
const EstimateCreate = lazy(() => import("../pages/Estimates/EstimateCreate"));
const EstimatePreview = lazy(() => import("../pages/Estimates/EstimatePreview"));

// Time Entries
const TimeEntriesList = lazy(() => import("../pages/TimeEntries/TimeEntriesList"));
const TimeEntryApprovals = lazy(() => import("../pages/TimeEntries/TimeEntryApprovals"));
const QuickTimeEntry = lazy(() => import("../pages/TimeEntries/QuickTimeEntry"));
const TimeSankey = lazy(() => import("../pages/TimeEntries/TimeSankey"));
const ConvertClock = lazy(() => import("../pages/TimeEntries/ConvertClock"));
const HoursCalendar = lazy(() => import("../pages/TimeTracking/HoursCalendar"));
const AllinErfassung = lazy(() => import("../pages/Zeiterfassung/AllinErfassung"));
const AllinErfassungProPerson = lazy(() => import("../pages/Zeiterfassung/AllinErfassungProPerson"));
const KontingentList = lazy(() => import("../pages/Zeiterfassung/KontingentList"));
const TimeTracker = lazy(() => import("../pages/TimeTracker"));

// Clock
const ClockHistory = lazy(() => import("../pages/Clock/ClockHistory"));
const ClockCorrectionsAdmin = lazy(() => import("../pages/Admin/ClockCorrectionsAdmin"));

// Tasks
const TaskBoardVelzon = lazy(() => import("../pages/Tasks/TaskBoardVelzon"));
const TaskBoardSimple = lazy(() => import("../pages/Tasks/TaskBoardSimple"));
const TaskBoardClean = lazy(() => import("../pages/Tasks/TaskBoardClean"));

// Password Manager
const PasswordManager = lazy(() => import("../pages/PasswordManager"));

// Customers
const CustomerListPage = lazy(() => import("../pages/Customers/CustomerListPage"));
const CustomerDetailPage = lazy(() => import("../pages/Customers/CustomerDetailPage"));
const KVControlling = lazy(() => import("../pages/Customers/KVControlling"));

// Users
const UserList = lazy(() => import("../pages/Users/UserList"));
const UserSettings = lazy(() => import("../pages/Users/UserSettings"));
const UserRoles = lazy(() => import("../pages/Users/UserRoles"));
const RoleDetail = lazy(() => import("../pages/Users/RoleDetail"));
const CreateRole = lazy(() => import("../pages/Users/CreateRole"));
const CreateUserWizard = lazy(() => import("../pages/Users/CreateUserWizard"));
const OnboardingDashboard = lazy(() => import("../pages/Users/OnboardingDashboard"));
const UserDevices = lazy(() => import("../pages/UserDevices"));
const TestImageUpload = lazy(() => import("../pages/Users/TestImageUpload"));

// HR Management
const HRIncidents = lazy(() => import("../pages/HRIncidents"));

// Document Templates
const DocumentTemplates = lazy(() => import("../pages/DocumentTemplates"));
const DocumentGenerator = lazy(() => import("../pages/DocumentTemplates/DocumentGenerator"));
// TemplateEditor removed - templates are now managed in code (backend/src/templates/documentTemplates.ts)

// Fleet Management
const FleetManagement = lazy(() => import("../pages/FleetManagement"));

// Absences
const VacationBalance = lazy(() => import("../pages/Absences/VacationBalance"));
const AbsenceCalendarPage = lazy(() => import("../pages/Absences/AbsenceCalendarPage"));
const UserYearView = lazy(() => import("../pages/Absences/UserYearView"));
const ContingentAdjustments = lazy(() => import("../pages/Absences/ContingentAdjustments"));
const HourAdjustments = lazy(() => import("../pages/Absences/HourAdjustments"));
const AbsenceRequest = lazy(() => import("../pages/Absences/AbsenceRequest"));
const AbsenceRequests = lazy(() => import("../pages/Absences/AbsenceRequests"));


// Buyouts
const BuyoutList = lazy(() => import("../pages/Buyouts").then(module => ({ default: module.BuyoutList })));
const BuyoutDetail = lazy(() => import("../pages/Buyouts").then(module => ({ default: module.BuyoutDetail })));

// Authentication
const Login = lazy(() => import("../pages/Authentication/Login"));
const ForgetPasswordPage = lazy(() => import("../pages/Authentication/ForgetPassword"));
const Register = lazy(() => import("../pages/Authentication/Register"));
const Logout = lazy(() => import("../pages/Authentication/Logout"));
const UserProfile = lazy(() => import("../pages/Authentication/user-profile"));

// External Pages (Public)
const ExternalExpenseSignature = lazy(() => import("../pages/External/ExternalExpenseSignature"));
const ExternalDocumentCapture = lazy(() => import("../pages/External/ExternalDocumentCapture"));

// Key Kiosk (Public)
const KeyKiosk = lazy(() => import("../pages/KeyKiosk"));

// Public Door (Public)
const PublicDoor = lazy(() => import("../pages/PublicDoor"));

// Break Kiosk (Public)
const BreakKiosk = lazy(() => import("../pages/Kiosk/BreakKiosk"));
const BreakKioskSimple = lazy(() => import("../pages/Kiosk/BreakKioskSimple"));

// Door Access Logs (Admin)
const DoorAccessLogs = lazy(() => import("../pages/DoorAccessLogs"));

// Dev Tools
const SqlImport = lazy(() => import("../pages/DevTools/SqlImport"));
const FrameIOTest = lazy(() => import("../pages/DevTools/FrameIO/FrameIOTest"));
const ApiTokens = lazy(() => import("../pages/DevTools/ApiTokens"));
const DatabaseInsights = lazy(() => import("../pages/DevTools/DatabaseInsights/DatabaseInsights"));
const KvGenerator = lazy(() => import("../pages/DevTools/KvGenerator"));
const BugReports = lazy(() => import("../pages/DevTools/BugReports"));
const VersionHistory = lazy(() => import("../pages/DevTools/VersionHistory"));
const FrameIOAssetExplorer = lazy(() => import("../pages/FrameIOAssetExplorer"));
const FrameIOUploader = lazy(() => import("../pages/FrameIOUploader"));
const FrameIOUploads = lazy(() => import("../pages/FrameIOUploads"));
const FrameIOMyReviews = lazy(() => import("../pages/FrameIOMyReviews"));
const CalculationSaves = lazy(() => import("../pages/DevTools/CalculationSaves"));
const PushNotificationTest = lazy(() => import("../pages/DevTools/PushNotificationTest"));
const LogViewer = lazy(() => import("../pages/DevTools/LogViewer"));
const CustomerDuplicateMigration = lazy(() => import("../pages/DevTools/CustomerDuplicateMigration"));
const BackupMonitor = lazy(() => import("../pages/DevTools/BackupMonitor"));
const UserPasswordOverride = lazy(() => import("../pages/DevTools/UserPasswordOverride"));
const NASStorage = lazy(() => import("../pages/NASStorage"));
const SpeakerSettings = lazy(() => import("../pages/DevTools/SpeakerSettings"));

// Google Calendar
const GoogleCalendar = lazy(() => import("../pages/GoogleCalendar"));

// Smart Office (Home Assistant)
const SmartOffice = lazy(() => import("../pages/SmartOffice"));

// Wiki
const WikiOverview = lazy(() => import("../pages/Wiki/WikiOverview"));
const WikiArticle = lazy(() => import("../pages/Wiki/WikiArticle"));

// Messages
const Messages = lazy(() => import("../pages/Messages/Messages"));

// Notes
const Notes = lazy(() => import("../pages/Notes"));
const NoteDetail = lazy(() => import("../pages/Notes/NoteDetail"));
const PromptDesigner = lazy(() => import("../pages/Notes/PromptDesigner"));

// Admin Dashboard
const AdminMonitoring = lazy(() => import("../pages/AdminDashboard/AdminMonitoring"));
const ClockManagement = lazy(() => import("../pages/Admin/ClockManagement"));
const NewsManagement = lazy(() => import("../pages/Admin/NewsManagement"));
const AISettings = lazy(() => import("../pages/Admin/AISettings"));
const ForgottenClocks = lazy(() => import("../pages/Admin/ForgottenClocks"));
const ExternalDoorAccess = lazy(() => import("../pages/Admin/ExternalDoorAccess"));

// Accounting
const PaymentTerminal = lazy(() => import("../pages/Accounting/PaymentTerminal"));

// News
const News = lazy(() => import("../pages/News"));

// Notifications
const Notifications = lazy(() => import("../pages/Notifications"));

// Errors
const NotFound404 = lazy(() => import("../pages/Errors/NotFound404"));

const authProtectedRoutes = [
  // Default route
  { path: "/", exact: true, component: <Navigate to="/base" /> },
  { path: "/dashboard", component: <Navigate to="/base" /> },
  { path: "/index", component: <Navigate to="/base" /> },
  
  // Dashboard
  { path: "/base", component: withSuspense(ExecutiveDashboard) },
  
  // Projects
  { path: "/projects", component: <ProtectedRoute menuId="projects1">{withSuspense(Projects)}</ProtectedRoute> },
  { path: "/projects/overview", component: <ProtectedRoute menuId="projectsoverview">{withSuspense(ProjectManagerOverview)}</ProtectedRoute> },
  { path: "/projects/overview/:managerId", component: <ProtectedRoute menuId="projectsoverview">{withSuspense(ProjectManagerOverview)}</ProtectedRoute> },
  { path: "/projects/pitch-please", component: <ProtectedRoute menuId="pitchplease">{withSuspense(PitchPlease)}</ProtectedRoute> },
  { path: "/projects/graveyard", component: <ProtectedRoute menuId="projectgraveyard">{withSuspense(ProjectGraveyard)}</ProtectedRoute> },
  { path: "/projects/create", component: <ProtectedRoute menuId="newproject">{withSuspense(ProjectTypeSelection)}</ProtectedRoute> },
  { path: "/projects/create-pitch", component: <ProtectedRoute menuId="newproject">{withSuspense(CreateProject)}</ProtectedRoute> },
  { path: "/projects/create-confirmed", component: <ProtectedRoute menuId="newproject">{withSuspense(CreateConfirmedProject)}</ProtectedRoute> },
  { path: "/projects/create-adaptation", component: <ProtectedRoute menuId="newproject">{withSuspense(CreateAdaptation)}</ProtectedRoute> },
  { path: "/projects/:id/edit", component: <ProtectedRoute menuId="projects1">{withSuspense(EditProject)}</ProtectedRoute> },
  { path: "/projects/:projectId/comments-test", component: <ProtectedRoute menuId="projects1">{withSuspense(ProjectCommentsTest)}</ProtectedRoute> },
  { path: "/projects/:id", component: <ProtectedRoute menuId="projects1">{withSuspense(ProjectOverview)}</ProtectedRoute> },
  
  // Calculations
  { path: "/calculations", component: <ProtectedRoute menuId="kalkulationlist">{withSuspense(KalkulationList)}</ProtectedRoute> },
  { path: "/calculations/:id", component: <ProtectedRoute menuId="kalkulationlist">{withSuspense(KalkulationDetail)}</ProtectedRoute> },
  { path: "/calculations-templates", component: <ProtectedRoute menuId="kalkulationtemplates">{withSuspense(CalculationTemplates)}</ProtectedRoute> },
  { path: "/calculations-templates/:id", component: <ProtectedRoute menuId="kalkulationtemplates">{withSuspense(CalculationTemplateEditor)}</ProtectedRoute> },
  { path: "/calculation-comments", component: <ProtectedRoute menuId="kalkulationcomments">{withSuspense(CalculationCommentsOverview)}</ProtectedRoute> },
  { path: "/calculations/hours", component: <ProtectedRoute menuId="projecthoursoverview">{withSuspense(ProjectHoursOverview)}</ProtectedRoute> },
  { path: "/calculations/hours/:userId", component: <ProtectedRoute menuId="projecthoursoverview">{withSuspense(EmployeeDetail)}</ProtectedRoute> },

  // Invoices
  { path: "/invoices", component: <ProtectedRoute menuId="invoiceslist">{withSuspense(InvoiceListPage)}</ProtectedRoute> },
  { path: "/invoices/new", component: <ProtectedRoute menuId="invoiceslist">{withSuspense(InvoiceNew)}</ProtectedRoute> },
  { path: "/invoices/create", component: <ProtectedRoute menuId="invoiceslist">{withSuspense(InvoiceNew)}</ProtectedRoute> },
  { path: "/invoices-create", component: <ProtectedRoute menuId="invoiceslist">{withSuspense(InvoiceNew)}</ProtectedRoute> },
  { path: "/invoices/:id", component: <ProtectedRoute menuId="invoiceslist">{withSuspense(InvoiceDetails)}</ProtectedRoute> },
  { path: "/invoices-detail/:id?", component: <ProtectedRoute menuId="invoiceslist">{withSuspense(InvoiceDetails)}</ProtectedRoute> },
  
  // Incoming Invoices
  { path: "/incoming-invoices", component: <ProtectedRoute menuId="incominginvoices">{withSuspense(IncomingInvoicesList)}</ProtectedRoute> },
  { path: "/my-approvals", component: <ProtectedRoute menuId="myapprovals">{withSuspense(MyApprovals)}</ProtectedRoute> },
  
  // Accounting
  { path: "/payment-terminal", component: <ProtectedRoute menuId="paymentterminal">{withSuspense(PaymentTerminal)}</ProtectedRoute> },
  
  // Document Capture
  { path: "/document-capture", component: <ProtectedRoute menuId="documentcapture">{withSuspense(DocumentCapture)}</ProtectedRoute> },
  { path: "/document-review", component: <ProtectedRoute menuId="documentreview">{withSuspense(DocumentReview)}</ProtectedRoute> },
  { path: "/my-documents", component: <ProtectedRoute menuId="mydocuments">{withSuspense(MyDocuments)}</ProtectedRoute> },
  
  // Emails
  { path: "/emails", component: <ProtectedRoute menuId="emails">{withSuspense(EmailInbox)}</ProtectedRoute> },
  
  // Receipts
  { path: "/receipts", component: <ProtectedRoute menuId="receipts">{withSuspense(Receipts)}</ProtectedRoute> },
  
  // Cash Book
  { path: "/cashbook", component: <ProtectedRoute menuId="cashbook">{withSuspense(CashBook)}</ProtectedRoute> },

  // Vendors
  { path: "/vendors", component: <ProtectedRoute menuId="vendors">{withSuspense(Vendors)}</ProtectedRoute> },

  // Expense Reports
  { path: "/expense-reports", component: <ProtectedRoute menuId="expensereports">{withSuspense(ExpenseReports)}</ProtectedRoute> },
  { path: "/expense-reports/:id", component: <ProtectedRoute menuId="expensereports">{withSuspense(ExpenseReportDetail)}</ProtectedRoute> },
  
  // Subscriptions
  { path: "/subscriptions", component: <ProtectedRoute menuId="subscriptions">{withSuspense(Subscriptions)}</ProtectedRoute> },
  { path: "/subscriptions-overview", component: <ProtectedRoute menuId="subscriptions">{withSuspense(SubscriptionsOverview)}</ProtectedRoute> },
  
  // Bank Statements
  { path: "/bank-statements", component: <ProtectedRoute menuId="bankstatements">{withSuspense(BankStatements)}</ProtectedRoute> },
  
  // Monthly Report
  { path: "/monthly-report", component: <ProtectedRoute menuId="monthlyreport">{withSuspense(MonthlyReport)}</ProtectedRoute> },
  { path: "/reports", component: <ProtectedRoute menuId="reports">{withSuspense(Reports)}</ProtectedRoute> },
  { path: "/reports/artlist", component: <ProtectedRoute menuId="artlist-reports">{withSuspense(ArtlistReports)}</ProtectedRoute> },
  { path: "/reports/portfolio", component: <ProtectedRoute menuId="portfolio-reports">{withSuspense(PortfolioReports)}</ProtectedRoute> },
  
  // Credit Cards
  { path: "/credit-cards", component: <ProtectedRoute menuId="creditcards">{withSuspense(CreditCards)}</ProtectedRoute> },
  { path: "/credit-cards/:creditCardId/statements", component: <ProtectedRoute menuId="creditcards">{withSuspense(CreditCardStatements)}</ProtectedRoute> },
  { path: "/credit-cards/statements/:statementId/transactions", component: <ProtectedRoute menuId="creditcards">{withSuspense(CreditCardTransactions)}</ProtectedRoute> },
  
  // Turnovers
  { path: "/turnovers", component: <ProtectedRoute menuId="turnovers">{withSuspense(TurnoverListPage)}</ProtectedRoute> },
  { path: "/turnovers/:projectId", component: <ProtectedRoute menuId="turnovers">{withSuspense(TurnoverListPage)}</ProtectedRoute> },
  
  // Payment Reminders
  { path: "/payment-reminders/overdue", component: <ProtectedRoute menuId="paymentreminders">{withSuspense(OverdueInvoices)}</ProtectedRoute> },
  { path: "/payment-reminders/invoice/:invoiceId", component: <ProtectedRoute menuId="paymentreminders">{withSuspense(PaymentRemindersManagement)}</ProtectedRoute> },
  { path: "/payment-reminders/pdf/:reminderId", component: <ProtectedRoute menuId="paymentreminders">{withSuspense(PaymentReminderPDF)}</ProtectedRoute> },
  
  // Estimates
  { path: "/estimates", component: <ProtectedRoute menuId="estimateslist">{withSuspense(EstimateList)}</ProtectedRoute> },
  { path: "/estimates/create", component: <ProtectedRoute menuId="estimateslist">{withSuspense(EstimateCreate)}</ProtectedRoute> },
  { path: "/estimates/preview", component: <ProtectedRoute menuId="estimateslist">{withSuspense(EstimatePreview)}</ProtectedRoute> },
  { path: "/estimates/settings", component: <ProtectedRoute menuId="estimatessettings">{withSuspense(EstimateSettings)}</ProtectedRoute> },
  { path: "/estimates/:id", component: <ProtectedRoute menuId="estimateslist">{withSuspense(EstimateDetail)}</ProtectedRoute> },
  
  // Time Entries
  { path: "/time-entries", component: <ProtectedRoute menuId="timeentries">{withSuspense(TimeEntriesList)}</ProtectedRoute> },
  { path: "/time-entries/approvals", component: withSuspense(TimeEntryApprovals) },
  { path: "/time-entries/quick", component: <ProtectedRoute menuId="quicktimeentry">{withSuspense(QuickTimeEntry)}</ProtectedRoute> },
  { path: "/time-entries/sankey", component: <ProtectedRoute menuId="timesankey">{withSuspense(TimeSankey)}</ProtectedRoute> },
  { path: "/time-entries/hours-calendar", component: <ProtectedRoute menuId="hourscalendar">{withSuspense(HoursCalendar)}</ProtectedRoute> },
  { path: "/time-entries/convert-clock", component: withSuspense(ConvertClock) },
  { path: "/time-entries/:id", component: <ProtectedRoute menuId="timeentries">{withSuspense(TimeEntriesList)}</ProtectedRoute> },
  { path: "/allin-erfassung", component: withSuspense(AllinErfassung) },
  { path: "/allin-erfassung-pro-person", component: withSuspense(AllinErfassungProPerson) },
  { path: "/kontingente", component: withSuspense(KontingentList) },
  { path: "/timetracker", component: <ProtectedRoute menuId="timetracker">{withSuspense(TimeTracker)}</ProtectedRoute> },
  
  // Tasks
  { path: "/tasks", component: <ProtectedRoute menuId="tasks">{withSuspense(TaskBoardClean)}</ProtectedRoute> },
  { path: "/tasks/simple", component: <ProtectedRoute menuId="tasks">{withSuspense(TaskBoardSimple)}</ProtectedRoute> },
  { path: "/tasks/old", component: <ProtectedRoute menuId="tasks">{withSuspense(TaskBoardVelzon)}</ProtectedRoute> },

  // Password Manager
  { path: "/password-manager", component: withSuspense(PasswordManager) },
  
  // Clock
  { path: "/clock/history", component: withSuspense(ClockHistory) },
  { path: "/admin/clock-corrections", component: withSuspense(ClockCorrectionsAdmin) },

  // Customers
  { path: "/customers", component: withSuspense(CustomerListPage) },
  { path: "/customers/kv-controlling", component: withSuspense(KVControlling) },
  { path: "/customers/:id", component: withSuspense(CustomerDetailPage) },
  
  // Users
  { path: "/users", component: withSuspense(UserList) },
  { path: "/users/create", component: withSuspense(CreateUserWizard) },
  { path: "/users/onboarding", component: withSuspense(OnboardingDashboard) },
  { path: "/users/test-upload", component: withSuspense(TestImageUpload) },
  { path: "/users/:id", component: withSuspense(UserSettings) },
  
  // HR Management
  { path: "/hr/incidents", component: withSuspense(HRIncidents) },

  // Document Templates (read-only, templates managed in code)
  { path: "/document-templates", component: withSuspense(DocumentTemplates) },
  { path: "/document-templates/:id", component: withSuspense(DocumentGenerator) },

  { path: "/user-roles", component: withSuspense(UserRoles) },
  { path: "/user-roles/new", component: withSuspense(CreateRole) },
  { path: "/user-roles/:id", component: withSuspense(RoleDetail) },
  { path: "/user-devices", component: withSuspense(UserDevices) },
  
  // Fleet Management
  { path: "/fleet-management", component: withSuspense(FleetManagement) },
  
  // Absences
  { path: "/absences/request", component: withSuspense(AbsenceRequest) },
  { path: "/absences/vacation-balance", component: withSuspense(VacationBalance) },
  { path: "/absences/calendar", component: withSuspense(AbsenceCalendarPage) },
  { path: "/absences/user/:userId/:year?", component: withSuspense(UserYearView) },
  { path: "/absences/adjustments", component: withSuspense(ContingentAdjustments) },
  { path: "/absences/hour-adjustments", component: withSuspense(HourAdjustments) },
  { path: "/absences/requests", component: withSuspense(AbsenceRequests) },
  
  // Buyouts
  { path: "/buyouts", component: withSuspense(BuyoutList) },
  { path: "/buyouts/new", component: withSuspense(BuyoutDetail) },
  { path: "/buyouts/:id", component: withSuspense(BuyoutDetail) },
  
  // Calendar
  { path: "/calendar", component: withSuspense(GoogleCalendar) },

  // Smart Office
  { path: "/smart-office", component: withSuspense(SmartOffice) },

  // Wiki
  { path: "/wiki", component: withSuspense(WikiOverview) },
  { path: "/wiki/:id", component: withSuspense(WikiArticle) },
  
  // Messages
  { path: "/messages", component: <ProtectedRoute menuId="messages">{withSuspense(Messages)}</ProtectedRoute> },
  
  // Notes
  { path: "/notes", component: withSuspense(Notes) },
  { path: "/notes/prompt-designer", component: withSuspense(PromptDesigner) },
  { path: "/notes/:id", component: withSuspense(NoteDetail) },
  
  // Dev Tools
  { path: "/dev-tools/sql-import", component: withSuspense(SqlImport) },
  { path: "/dev-tools/frameio", component: withSuspense(FrameIOTest) },
  { path: "/dev-tools/api-tokens", component: withSuspense(ApiTokens) },
  { path: "/dev-tools/database-insights", component: withSuspense(DatabaseInsights) },
  { path: "/dev-tools/kv-generator", component: withSuspense(KvGenerator) },
  { path: "/dev-tools/bug-reports", component: withSuspense(BugReports) },
  { path: "/admin/door-access-logs", component: withSuspense(DoorAccessLogs) },
  { path: "/dev-tools/calculation-saves", component: withSuspense(CalculationSaves) },
  { path: "/dev-tools/push-notifications", component: withSuspense(PushNotificationTest) },
  { path: "/dev-tools/logs", component: withSuspense(LogViewer) },
  { path: "/dev-tools/customer-duplicates", component: withSuspense(CustomerDuplicateMigration) },
  { path: "/dev-tools/backup-monitor", component: withSuspense(BackupMonitor) },
  { path: "/dev-tools/user-password-override", component: withSuspense(UserPasswordOverride) },
  { path: "/dev-tools/nas-storage", component: withSuspense(NASStorage) },
  { path: "/dev-tools/speaker", component: withSuspense(SpeakerSettings) },
  { path: "/version-history", component: withSuspense(VersionHistory) },
  { path: "/dev-tools/frameio-assets", component: withSuspense(FrameIOAssetExplorer) },
  { path: "/dev-tools/frameio-uploader", component: withSuspense(FrameIOUploader) },
  { path: "/dev-tools/frameio-uploads", component: withSuspense(FrameIOUploads) },
  { path: "/frameio/my-reviews", component: withSuspense(FrameIOMyReviews) },

  // Admin
  { path: "/admin/monitoring", component: withSuspense(AdminMonitoring) },
  { path: "/admin/clock-management", component: withSuspense(ClockManagement) },
  { path: "/admin/forgotten-clocks", component: withSuspense(ForgottenClocks) },
  { path: "/admin/news", component: withSuspense(NewsManagement) },
  { path: "/admin/ai-settings", component: withSuspense(AISettings) },
  { path: "/admin/external-door-access", component: withSuspense(ExternalDoorAccess) },

  // News
  { path: "/news", component: withSuspense(News) },

  // Profile
  { path: "/profile", component: withSuspense(UserProfile) },
  
  // Notifications
  { path: "/notifications", component: withSuspense(Notifications) },
  
  // 404 - This should be the last route
  { path: "*", component: withSuspense(NotFound404) },
];

const publicRoutes = [
  // Authentication
  { path: "/logout", component: withSuspense(Logout) },
  { path: "/login", component: withSuspense(Login) },
  { path: "/forgot-password", component: withSuspense(ForgetPasswordPage) },
  { path: "/register", component: withSuspense(Register) },
  
  // External Expense (Public)
  { path: "/external/expense/:token", component: withSuspense(ExternalExpenseSignature) },
  { path: "/external/expense/:token/upload", component: withSuspense(ExternalDocumentCapture) },

  // Key Kiosk (Public)
  { path: "/public/keys", component: withSuspense(KeyKiosk) },
  { path: "/public/keys/menu", component: withSuspense(KeyKiosk) },
  { path: "/public/keys/select", component: withSuspense(KeyKiosk) },

  // Public Door (Public)
  { path: "/public/door", component: withSuspense(PublicDoor) },
  { path: "/public/keys/confirm", component: withSuspense(KeyKiosk) },
  { path: "/public/keys/success", component: withSuspense(KeyKiosk) },
  { path: "/public/keys/external", component: withSuspense(KeyKiosk) },
  { path: "/public/keys/handoff", component: withSuspense(KeyKiosk) },

  // Break Kiosk (Public)
  { path: "/public/entry", component: withSuspense(BreakKiosk) },
  { path: "/public/entry-simple", component: withSuspense(BreakKioskSimple) },
];

export { authProtectedRoutes, publicRoutes };